/* tslint:disable */
/* eslint-disable */
import * as runtime from '../runtime';
import type { CourierReview, CourierReviewPublic, CourierReviewSummary } from '../models/index';
import {
    CourierReviewFromJSON,
    CourierReviewToJSON,
    CourierReviewPublicFromJSON,
    CourierReviewSummaryFromJSON,
} from '../models/index';

export interface CourierReviewFindRequest {
    filter?: string;
}

export interface CourierReviewCountRequest {
    where?: string;
}

export interface CourierReviewFindByIdRequest {
    id: number;
    filter?: string;
}

export interface CourierReviewPatchAttributesRequest {
    id: number;
    data: Partial<CourierReview>;
}

export interface CourierReviewDeleteByIdRequest {
    id: number;
}

export interface CourierReviewSubmitRequest {
    data: { rating: number; body: string; title?: string; packageId?: number };
}

export interface CourierReviewModerateRequest {
    id: number;
    data: { decision: 'approved' | 'rejected'; reviewNotes?: string };
}

export interface CourierReviewCreateOnBehalfRequest {
    data: {
        customerId?: number;
        customerName: string;
        customerEmail?: string;
        rating: number;
        title?: string;
        body: string;
        packageId?: number;
        approve?: boolean;
    };
}

export interface CourierReviewPublicListRequest {
    courierId: number;
    limit?: number;
    skip?: number;
}

export interface CourierReviewPublicSummaryRequest {
    courierId: number;
}

/**
 * Customer reviews: admin CRUD + moderation, customer submit/mine, public list/summary.
 */
export class CourierReviewApi extends runtime.BaseAPI {

    async courierReviewFindRaw(requestParameters: CourierReviewFindRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<CourierReview>>> {
        const queryParameters: any = {};
        if (requestParameters['filter'] != null) {
            queryParameters['filter'] = requestParameters['filter'];
        }
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/CourierReviews`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CourierReviewFromJSON));
    }

    async courierReviewFind(requestParameters: CourierReviewFindRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<CourierReview>> {
        const response = await this.courierReviewFindRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async courierReviewCountRaw(requestParameters: CourierReviewCountRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<{ count: number }>> {
        const queryParameters: any = {};
        if (requestParameters['where'] != null) {
            queryParameters['where'] = requestParameters['where'];
        }
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/CourierReviews/count`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response);
    }

    async courierReviewCount(requestParameters: CourierReviewCountRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<{ count: number }> {
        const response = await this.courierReviewCountRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async courierReviewFindByIdRaw(requestParameters: CourierReviewFindByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CourierReview>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError('id', 'Required parameter "id" was null or undefined when calling courierReviewFindById().');
        }
        const queryParameters: any = {};
        if (requestParameters['filter'] != null) {
            queryParameters['filter'] = requestParameters['filter'];
        }
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/CourierReviews/${encodeURIComponent(String(requestParameters['id']))}`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => CourierReviewFromJSON(jsonValue));
    }

    async courierReviewFindById(requestParameters: CourierReviewFindByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CourierReview> {
        const response = await this.courierReviewFindByIdRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async courierReviewPatchAttributesRaw(requestParameters: CourierReviewPatchAttributesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CourierReview>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError('id', 'Required parameter "id" was null or undefined when calling courierReviewPatchAttributes().');
        }
        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';
        const response = await this.request({
            path: `/CourierReviews/${encodeURIComponent(String(requestParameters['id']))}`,
            method: 'PATCH',
            headers: headerParameters,
            query: queryParameters,
            body: CourierReviewToJSON(requestParameters['data']),
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => CourierReviewFromJSON(jsonValue));
    }

    async courierReviewPatchAttributes(requestParameters: CourierReviewPatchAttributesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CourierReview> {
        const response = await this.courierReviewPatchAttributesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async courierReviewDeleteByIdRaw(requestParameters: CourierReviewDeleteByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<object>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError('id', 'Required parameter "id" was null or undefined when calling courierReviewDeleteById().');
        }
        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/CourierReviews/${encodeURIComponent(String(requestParameters['id']))}`,
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response);
    }

    async courierReviewDeleteById(requestParameters: CourierReviewDeleteByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<object> {
        const response = await this.courierReviewDeleteByIdRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /** Customer (User role) submits a review; lands as status=pending. */
    async courierReviewSubmitRaw(requestParameters: CourierReviewSubmitRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CourierReview>> {
        if (requestParameters['data'] == null) {
            throw new runtime.RequiredError('data', 'Required parameter "data" was null or undefined when calling courierReviewSubmit().');
        }
        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';
        const response = await this.request({
            path: `/CourierReviews/submit`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: requestParameters['data'],
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => CourierReviewFromJSON(jsonValue));
    }

    async courierReviewSubmit(requestParameters: CourierReviewSubmitRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CourierReview> {
        const response = await this.courierReviewSubmitRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /** The calling customer's own reviews (any status), newest first. */
    async courierReviewMineRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<CourierReview>>> {
        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/CourierReviews/mine`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CourierReviewFromJSON));
    }

    async courierReviewMine(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<CourierReview>> {
        const response = await this.courierReviewMineRaw(initOverrides);
        return await response.value();
    }

    /** Admin approves or rejects a review. */
    async courierReviewModerateRaw(requestParameters: CourierReviewModerateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CourierReview>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError('id', 'Required parameter "id" was null or undefined when calling courierReviewModerate().');
        }
        if (requestParameters['data'] == null) {
            throw new runtime.RequiredError('data', 'Required parameter "data" was null or undefined when calling courierReviewModerate().');
        }
        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';
        const response = await this.request({
            path: `/CourierReviews/${encodeURIComponent(String(requestParameters['id']))}/moderate`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: requestParameters['data'],
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => CourierReviewFromJSON(jsonValue));
    }

    async courierReviewModerate(requestParameters: CourierReviewModerateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CourierReview> {
        const response = await this.courierReviewModerateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /** Admin adds a review on behalf of a customer (source=admin; approved by default). */
    async courierReviewCreateOnBehalfRaw(requestParameters: CourierReviewCreateOnBehalfRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CourierReview>> {
        if (requestParameters['data'] == null) {
            throw new runtime.RequiredError('data', 'Required parameter "data" was null or undefined when calling courierReviewCreateOnBehalf().');
        }
        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';
        const response = await this.request({
            path: `/CourierReviews/createOnBehalf`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: requestParameters['data'],
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => CourierReviewFromJSON(jsonValue));
    }

    async courierReviewCreateOnBehalf(requestParameters: CourierReviewCreateOnBehalfRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CourierReview> {
        const response = await this.courierReviewCreateOnBehalfRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /** Approved reviews for a courier (anonymous; x-api-key only). */
    async courierReviewPublicListRaw(requestParameters: CourierReviewPublicListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<CourierReviewPublic>>> {
        if (requestParameters['courierId'] == null) {
            throw new runtime.RequiredError('courierId', 'Required parameter "courierId" was null or undefined when calling courierReviewPublicList().');
        }
        const queryParameters: any = { courierId: requestParameters['courierId'] };
        if (requestParameters['limit'] != null) queryParameters['limit'] = requestParameters['limit'];
        if (requestParameters['skip'] != null) queryParameters['skip'] = requestParameters['skip'];
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/CourierReviews/public`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CourierReviewPublicFromJSON));
    }

    async courierReviewPublicList(requestParameters: CourierReviewPublicListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<CourierReviewPublic>> {
        const response = await this.courierReviewPublicListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async courierReviewPublicSummaryRaw(requestParameters: CourierReviewPublicSummaryRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CourierReviewSummary>> {
        if (requestParameters['courierId'] == null) {
            throw new runtime.RequiredError('courierId', 'Required parameter "courierId" was null or undefined when calling courierReviewPublicSummary().');
        }
        const queryParameters: any = { courierId: requestParameters['courierId'] };
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/CourierReviews/public-summary`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => CourierReviewSummaryFromJSON(jsonValue));
    }

    async courierReviewPublicSummary(requestParameters: CourierReviewPublicSummaryRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CourierReviewSummary> {
        const response = await this.courierReviewPublicSummaryRaw(requestParameters, initOverrides);
        return await response.value();
    }
}
